import axios from 'axios';
import { getErrorMessage, HTTP_ERROR_MESSAGES } from '../utils/errorMessages';

const API_URL = process.env.REACT_APP_API_URL || 'https://hashimoto-backend01.onrender.com';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 120000,  // 2分
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Disable cache for all API calls
  config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  config.headers['Pragma'] = 'no-cache';
  config.headers['Expires'] = '0';

  // Add timestamp for cache busting on GET requests
  if (config.method === 'get') {
    config.params = config.params || {};
    config.params._t = Date.now();
  }

  return config;
});

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Override axios default error message only if backend didn't provide a message
    if (error.message && error.message.startsWith('Request failed with status code')) {
      // First check if backend provided a proper error message
      if (error.response?.data?.error) {
        error.message = error.response.data.error;
      } else {
        // Fallback to generic status code messages
        const statusCode = error.response?.status;
        if (statusCode) {
          const japaneseMessage = HTTP_ERROR_MESSAGES[statusCode] || '予期しないエラーが発生しました。';
          error.message = japaneseMessage;
        }
      }
    }

    // Skip 401 handling for admin API calls (admin uses X-Admin-Token, not user JWT)
    const isAdminApi = originalRequest?.url?.includes('/admin/');

    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest._retry && !isAdminApi) {
      // Prevent infinite loops
      originalRequest._retry = true;

      // Check if it's a plan mismatch error
      if (error.response?.data?.code === 'PLAN_MISMATCH') {
        
        // Try to refresh the token to get updated plan information
        try {
          const currentToken = localStorage.getItem('token');
          if (currentToken) {
            // Use a separate axios instance to avoid interceptor loop
            const refreshResponse = await axios.post(
              `${API_URL}/api/auth/refresh`,
              {},
              {
                headers: {
                  'Authorization': `Bearer ${currentToken}`
                }
              }
            );
            
            const { token } = refreshResponse.data;
            
            // Save new token with updated plan
            localStorage.setItem('token', token);
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
        }
      }

      // Clear token and redirect to login for all 401 errors that can't be recovered
      localStorage.removeItem('token');

      // Only redirect if we're not already on the login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }

      // Use backend error message if available
      if (error.response?.data?.error) {
        error.userMessage = error.response.data.error;
      }
      return Promise.reject(error);
    }

    // Enhance error with Japanese message for better error handling
    if (error.response) {
      // Add user-friendly Japanese error message
      const userFriendlyMessage = getErrorMessage(error);
      error.userMessage = userFriendlyMessage;
      
      // Log error for debugging
    } else if (error.request) {
      // Network error
      error.userMessage = 'インターネット接続をご確認ください。';
    } else {
      // Other errors
      error.userMessage = '予期しないエラーが発生しました。しばらく時間をおいてから再試行してください。';
    }

    return Promise.reject(error);
  }
);

export { API_URL };
export default api;

